import React, { useEffect, useMemo, useState, useContext, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, Reorder } from "framer-motion";
import { CloseRounded, CheckCircle, HighlightOff, Restore } from "@mui/icons-material";
import NeoButton from "./NeoButton";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";
import { dialogPaperSx, dialogTitleSx, dialogTitleTextSx, dialogCloseButtonSx } from "../@shared/ui/tokens";

// bring your constant from where you define it
const standardEmotes = [
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/gifs%2Fgg.gif?alt=media&token=065cd182-dd8b-46da-b626-302fcb54e359",
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/gifs%2Fretro-shades.gif?alt=media&token=e9a36f65-ad04-45df-9ace-5b8b0c5ab5d9",
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/gifs%2Frip.gif?alt=media&token=cfcc6f22-1149-411c-a0d7-0943e75a6340",
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/gifs%2Ftrophy.gif?alt=media&token=faa26c1a-0c1a-429e-92c3-fd97ca935ef3",
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/gifs%2Ffire.gif?alt=media&token=dcc5e166-2386-4596-b210-e163334f1375",
];

// --------------------
// Horizontal scroller helpers (wheel -> horizontal, click-drag, touch)
// --------------------
const useHorizontalScroller = () => {
  const ref = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const dragStateRef = useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
  });

  const updateFadeMasks = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const epsilon = 2;
    setCanScrollLeft(scrollLeft > epsilon);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - epsilon);
  }, []);

  const onWheel = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (el.scrollWidth > el.clientWidth) {
        e.preventDefault();
        el.scrollLeft += delta;
        updateFadeMasks();
      }
    },
    [updateFadeMasks]
  );

  const dragStart = (clientX) => {
    const el = ref.current;
    if (!el) return;
    dragStateRef.current.isDown = true;
    dragStateRef.current.startX = clientX;
    dragStateRef.current.startScrollLeft = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  };

  const dragMove = (clientX) => {
    const el = ref.current;
    if (!el) return;
    if (!dragStateRef.current.isDown) return;
    const dx = clientX - dragStateRef.current.startX;
    el.scrollLeft = dragStateRef.current.startScrollLeft - dx;
    updateFadeMasks();
  };

  const dragEnd = () => {
    const el = ref.current;
    if (!el) return;
    dragStateRef.current.isDown = false;
    el.style.cursor = "grab";
    el.style.userSelect = "auto";
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    dragStart(e.clientX);
  };
  const onMouseMove = (e) => dragMove(e.clientX);
  const onMouseUp = () => dragEnd();
  const onMouseLeave = () => dragEnd();

  const onTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    dragStart(e.touches[0].clientX);
  };
  const onTouchMove = (e) => {
    if (!e.touches?.[0]) return;
    dragMove(e.touches[0].clientX);
  };
  const onTouchEnd = () => dragEnd();

  return {
    ref,
    canScrollLeft,
    canScrollRight,
    updateFadeMasks,
    bind: {
      onScroll: updateFadeMasks,
      onWheel,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
  };
};

const EmoteCard = ({ url, name, selected, disabled, onClick, i = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: i * 0.03 }}
  >
    <Box
      onClick={() => !disabled && onClick()}
      sx={{
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative",
        border: selected ? "2px solid #ffd24d" : "2px solid rgba(255,255,255,0.10)",
        borderRadius: 2.5,
        overflow: "hidden",
        backgroundColor: selected ? "rgba(255,210,77,0.10)" : "rgba(0,0,0,0.25)",
        width: 108,
        height: 108,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "0.15s",
        opacity: disabled ? 0.55 : 1,
        "&:hover": { transform: disabled ? "none" : "translateY(-2px)" },
        "&:active": { transform: disabled ? "none" : "scale(0.99)" },
      }}
    >
      <img
        src={url}
        alt={name || "emote"}
        style={{
          width: 92,
          height: 92,
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />
      {selected && (
        <CheckCircle
          style={{ position: "absolute", top: 6, right: 6, color: "#ffd24d" }}
          fontSize="small"
        />
      )}
    </Box>
  </motion.div>
);

const SelectedEmotePill = ({ url, onRemove }) => (
  <Reorder.Item value={url}>
    <Box
      sx={{
        p: 1,
        px: 1.25,
        bgcolor: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 2.5,
        display: "flex",
        alignItems: "center",
        gap: 1,
        flex: "0 0 auto",
      }}
    >
      <img
        src={url}
        alt="selected-emote"
        style={{ width: 34, height: 34, objectFit: "contain" }}
      />
      <IconButton size="small" onClick={() => onRemove(url)} sx={{ color: "#e2d5ff" }}>
        <HighlightOff fontSize="small" />
      </IconButton>
    </Box>
  </Reorder.Item>
);

const EmoteManagerModal = ({ open, onClose }) => {
  const { userData } = useAuthStore();
  const { Theme, setSuccess, setError, setMessage } = useContext(MainContext);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [owned, setOwned] = useState([]);
  const [selected, setSelected] = useState([]);

  const selectedScroller = useHorizontalScroller();
  const catalogScroller = useHorizontalScroller();

  useEffect(() => {
    if (!open) return;
    const initSelected =
      userData?.emotes?.length
        ? [...new Set(userData.emotes)].slice(0, 5)
        : standardEmotes.slice(0, 5);
    setSelected(initSelected);
  }, [open, userData?.emotes]);

  useEffect(() => {
    const fetchOwned = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "users", userData.id, "emotes"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOwned(list.filter((e) => (e?.amountOwned ?? 0) > 0));
      } catch (e) {
        console.error("Error loading emotes", e);
        setError?.(true);
        setMessage?.("Unable to load your emotes.");
      } finally {
        setLoading(false);
        requestAnimationFrame(() => {
          selectedScroller.updateFadeMasks();
          catalogScroller.updateFadeMasks();
        });
      }
    };
    if (open) fetchOwned();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const catalog = useMemo(() => {
    const std = standardEmotes.map((url, i) => ({
      id: `std:${i}`,
      name: "Standard",
      image: url,
      source: "standard",
    }));
    const map = new Map(std.map((e) => [e.image, e]));
    for (const e of owned) {
      if (!map.has(e.image)) map.set(e.image, { ...e, source: "owned" });
    }
    return Array.from(map.values());
  }, [owned]);

  const toggleSelect = (url) => {
    setSelected((prev) => {
      const exists = prev.includes(url);
      if (exists) return prev.filter((u) => u !== url);
      if (prev.length >= 5) {
        setError?.(true);
        setMessage?.("You can choose up to 5 emotes.");
        return prev;
      }
      return [...prev, url];
    });
  };

  const removeSelected = (url) => setSelected((prev) => prev.filter((u) => u !== url));

  const save = async () => {
    try {
      const clean = [...new Set(selected)].slice(0, 5);
      await updateDoc(doc(db, "users", userData.id), { emotes: clean });
      setSuccess?.(true);
      setMessage?.("Emotes updated!");
      onClose?.();
    } catch (e) {
      console.error(e);
      setError?.(true);
      setMessage?.("Failed to save emotes.");
    }
  };

  const useDefaults = () => setSelected(standardEmotes.slice(0, 5));
  const clearAll = () => setSelected([]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      selectedScroller.updateFadeMasks();
      catalogScroller.updateFadeMasks();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, selectedScroller, catalogScroller]);

  if (!open) return null;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{
        onEntered: () =>
          requestAnimationFrame(() => {
            selectedScroller.updateFadeMasks();
            catalogScroller.updateFadeMasks();
          }),
      }}
      PaperProps={{
        sx: {
          ...dialogPaperSx,
        },
      }}
    >
      <DialogTitle
        sx={dialogTitleSx}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={dialogTitleTextSx}>
            Choose Your Emotes (max 5)
          </Typography>

          <IconButton size="small" onClick={onClose} sx={dialogCloseButtonSx}>
            <CloseRounded />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background: "radial-gradient(circle at top, #3b1f8a33 0%, transparent 55%)",
          p: 0,
        }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, minHeight: isMobile ? "65vh" : 520 }}>
          {/* Selected / reorder bar */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(0,0,0,0.25)",
            }}
          >
            <Typography sx={{ color: "#bdbdde", fontSize: 13, mb: 1 }}>
              Drag to reorder your in-match emote wheel.
            </Typography>

            <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden" }}>
              {/* Fade masks */}
              <Box sx={{ pointerEvents: "none", position: "absolute", inset: 0, zIndex: 2 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: 36,
                    opacity: selectedScroller.canScrollLeft ? 1 : 0,
                    transition: "opacity 200ms ease",
                    background:
                      "linear-gradient(90deg, rgba(10,6,24,0.95) 0%, rgba(10,6,24,0) 100%)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    height: "100%",
                    width: 36,
                    opacity: selectedScroller.canScrollRight ? 1 : 0,
                    transition: "opacity 200ms ease",
                    background:
                      "linear-gradient(270deg, rgba(10,6,24,0.95) 0%, rgba(10,6,24,0) 100%)",
                  }}
                />
              </Box>

              <Reorder.Group
                axis="x"
                values={selected}
                onReorder={setSelected}
                ref={selectedScroller.ref}
                {...selectedScroller.bind}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: 10,
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollbarWidth: "none",
                  cursor: "grab",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {selected.map((url) => (
                  <SelectedEmotePill key={url} url={url} onRemove={removeSelected} />
                ))}
              </Reorder.Group>
            </Box>

            <Box sx={{ display: "flex", gap: 1.25, mt: 1.25, flexWrap: "wrap", alignItems: "center" }}>
              <Chip
                icon={<Restore />}
                label="Use Defaults"
                onClick={useDefaults}
                sx={{ bgcolor: "rgba(0,0,0,0.35)", color: "#fff", border: "1px solid rgba(255,255,255,0.10)" }}
              />
              <Chip
                icon={<HighlightOff />}
                label="Clear All"
                onClick={clearAll}
                sx={{ bgcolor: "rgba(0,0,0,0.35)", color: "#fff", border: "1px solid rgba(255,255,255,0.10)" }}
              />
              <Chip
                label={`${selected.length}/5 selected`}
                sx={{
                  bgcolor: "rgba(255,210,77,0.12)",
                  color: "#ffd24d",
                  border: "1px solid rgba(255,210,77,0.25)",
                  ml: "auto",
                  fontWeight: 800,
                }}
              />
            </Box>
          </Box>

          {/* Catalog */}
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(0,0,0,0.25)",
              overflow: "hidden",
              position: "relative",
              minHeight: 240,
            }}
          >
            <Typography sx={{ color: "#bdbdde", fontSize: 13, mb: 1 }}>
              Available Emotes
            </Typography>

            {/* Fade masks */}
            <Box sx={{ pointerEvents: "none", position: "absolute", inset: 0, zIndex: 2 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 34,
                  left: 0,
                  height: "calc(100% - 34px)",
                  width: 36,
                  opacity: catalogScroller.canScrollLeft ? 1 : 0,
                  transition: "opacity 200ms ease",
                  background:
                    "linear-gradient(90deg, rgba(10,6,24,0.95) 0%, rgba(10,6,24,0) 100%)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 34,
                  right: 0,
                  height: "calc(100% - 34px)",
                  width: 36,
                  opacity: catalogScroller.canScrollRight ? 1 : 0,
                  transition: "opacity 200ms ease",
                  background:
                    "linear-gradient(270deg, rgba(10,6,24,0.95) 0%, rgba(10,6,24,0) 100%)",
                }}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              <Box
                ref={catalogScroller.ref}
                {...catalogScroller.bind}
                sx={{
                  mt: 0.5,
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                  cursor: "grab",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, p: 1, alignItems: "flex-start" }}>
                  {catalog.map((e, i) => {
                    const selectedAlready = selected.includes(e.image);
                    const disabled = !selectedAlready && selected.length >= 5;
                    return (
                      <Box key={e.image} sx={{ flex: "0 0 auto" }}>
                        <EmoteCard
                          url={e.image}
                          name={e.name}
                          selected={selectedAlready}
                          disabled={disabled}
                          onClick={() => toggleSelect(e.image)}
                          i={i}
                        />
                        <Typography
                          sx={{
                            mt: 0.75,
                            fontSize: 12,
                            textAlign: "center",
                            color: selectedAlready ? "#ffd24d" : "#e2d5ff",
                            fontWeight: selectedAlready ? 800 : 600,
                            opacity: 0.95,
                          }}
                        >
                          {e?.source === "standard" ? "Standard" : (e?.name || "Owned")}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: "#170b3b",
          borderTop: "1px solid #120a3a",
          p: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
        }}
      >
        <NeoButton text="Cancel" onClick={onClose} color={Theme?.palette?.secondary?.main || "#7b1fa2"} />
        <NeoButton text="Save Emotes" onClick={save} color={Theme?.palette?.primary?.main || "#1976d2"} />
      </DialogActions>
    </Dialog>
  );
};

export default EmoteManagerModal;
